import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2 } from "lucide-react";
import { API } from "@/config/api";

const CheckoutSuccess_v2 = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      navigate('/select-tier');
      return;
    }

    const pollPaymentStatus = async (attempt = 0) => {
      if (attempt >= maxAttempts) {
        setStatus('timeout');
        return;
      }

      try {
        const response = await axios.get(`${API}/subscription/status/${sessionId}`, {
          withCredentials: true
        });

        if (response.data.payment_status === 'paid') {
          setStatus('success');
        } else if (response.data.status === 'expired') {
          setStatus('expired');
        } else {
          setTimeout(() => {
            setAttempts(attempt + 1);
            pollPaymentStatus(attempt + 1);
          }, 2000);
        }
      } catch (error) {
        setStatus('error');
      }
    };

    pollPaymentStatus();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="bg-slate-800 rounded-3xl shadow-2xl border border-slate-700 p-12 max-w-md w-full text-center">
        {status === 'checking' && (
          <>
            <Loader2 className="h-16 w-16 text-yellow-400 mx-auto mb-6 animate-spin" />
            <h1 className="text-3xl font-heading font-bold mb-4 text-white" data-testid="checking-payment">
              Confirming Your Subscription
            </h1>
            <p className="text-slate-400 mb-8">
              Please wait while we verify your payment with Stripe...
            </p>
            <p className="text-sm text-slate-500">
              Attempt {attempts + 1} of {maxAttempts}
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 rounded-full bg-yellow-400/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="h-12 w-12 text-yellow-400" />
            </div>
            <h1 className="text-3xl font-heading font-bold mb-4 text-white" data-testid="success-message">
              Welcome to DueDay!
            </h1>
            <p className="text-slate-400 mb-8">
              Your subscription is now active. You're protected from late fees and service disconnections on essential bills.
            </p>
            <Button 
              data-testid="goto-dashboard-btn"
              onClick={() => navigate('/dashboard')}
              className="bg-yellow-400 text-black hover:bg-yellow-300 rounded-full px-8 py-6 text-lg font-bold w-full"
            >
              Go to Dashboard
            </Button>
          </>
        )}

        {(status === 'error' || status === 'timeout' || status === 'expired') && (
          <>
            <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
              <span className="text-4xl">❌</span>
            </div>
            <h1 className="text-3xl font-heading font-bold mb-4 text-white" data-testid="error-message">
              Payment Verification Issue
            </h1>
            <p className="text-slate-400 mb-8">
              We couldn't verify your payment. Please check your email for confirmation or contact support.
            </p>
            <div className="flex flex-col gap-3">
              <Button 
                data-testid="retry-btn"
                onClick={() => navigate('/select-tier')}
                className="bg-yellow-400 text-black hover:bg-yellow-300 rounded-full px-8 py-6 text-lg font-bold w-full"
              >
                Try Again
              </Button>
              <Button 
                data-testid="goto-dashboard-alt-btn"
                onClick={() => navigate('/dashboard')}
                variant="outline"
                className="border-slate-700 text-white hover:bg-slate-700 rounded-full px-8 py-6 text-lg font-bold w-full"
              >
                Go to Dashboard
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CheckoutSuccess_v2;
